import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, TrendingUp, TrendingDown, Search, Filter, Download } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useToast } from '@/components/ui/MobileToast'
import { businessService } from '@/services/businessService'

export function PortfolioPage() {
  const [isLoading, setIsLoading] = useState(true)
  const [businesses, setBusinesses] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [filterBand, setFilterBand] = useState('')
  const navigate = useNavigate()
  const showToast = useToast()

  useEffect(() => {
    loadPortfolio()
  }, [])

  const loadPortfolio = async () => {
    setIsLoading(true)
    try {
      const response = await businessService.getBusinesses({ limit: 50 })
      setBusinesses(response.businesses)
    } catch (error) {
      console.error('Failed to load portfolio:', error)
      showToast.showToast({
        type: 'error',
        title: 'Load Failed',
        message: 'Unable to load portfolio data.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const filteredBusinesses = businesses.filter(b => {
    const matchesSearch = !searchQuery || 
      b.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.pan.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesBand = !filterBand || b.band === filterBand
    return matchesSearch && matchesBand
  })

  const getRiskColor = (score: string) => {
    const s = parseFloat(score || '0')
    if (s >= 75) return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
    if (s >= 55) return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
    return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
  }

  const getBandColor = (band: string) => {
    switch (band) {
      case 'A+':
      case 'A': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
      case 'B+':
      case 'B': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900'
      case 'C+':
      case 'C': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
      default: return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading portfolio...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Portfolio Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage and monitor your business portfolio ({filteredBusinesses.length} businesses)
          </p>
        </div>
        <button
          onClick={() => showToast.showToast({ type: 'info', title: 'Export', message: 'Exporting portfolio data...' })}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Download className="w-4 h-4" />
          Export Portfolio
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or PAN..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <select
            value={filterBand}
            onChange={(e) => setFilterBand(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Bands</option>
            <option value="A+">A+</option>
            <option value="A">A</option>
            <option value="B+">B+</option>
            <option value="B">B</option>
            <option value="C+">C+</option>
            <option value="C">C</option>
            <option value="D">D</option>
          </select>
        </div>
      </div>

      {/* Portfolio Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBusinesses.map((business) => (
          <div
            key={business.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate(`/lender/business/${business.id}`)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getBandColor(business.band)}`}>
                {business.band}
              </div>
            </div>

            <h3 className="font-semibold text-gray-900 dark:text-white mb-2 truncate">
              {business.business_name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {business.industry_sector}
            </p>

            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">TLBH Score</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {parseFloat(business.score || '0').toFixed(2)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-600 dark:text-gray-400">Confidence</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {parseFloat(business.confidence || '0').toFixed(0)}%
                </p>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">PAN: {business.pan}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Last Updated: {new Date(business.calculated_at || business.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        ))}
      </div>

      {filteredBusinesses.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No businesses found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}
    </div>
  )
}